import { ApplicationElement, html } from '@crossiety/lit-components';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import '@crossiety/lit-components/components/avatar/element.js';
import '@crossiety/lit-components/components/icon/element.js';
import '@crossiety/lit-components/components/online_status/element.js';

import style from './style.lit.css';

/**
 * * Profile avatar is used to represent a Crossiety user.
 * * You should always provide a slot with a name.
 * * By default, a initials will be shown but you can personalize avatar by adding custom image.
 * * It can have an edit button or online status indicator.
 *
 * @prop editable - Add the editable attribute to show an edit button.
 * @prop hideStatus - Hide online status
 * @prop hideName - Hide name bellow ```xs-avatar```
 * @prop label - Add label for ```xs-avatar```
 * @prop layout - Predefined element size (options: xxs, xs, sm, md, lg)
 * @prop online - When true, shows online status
 * @prop picture - Add picture URL for ```xs-avatar```
 * @prop icon - Override default icon eg. bot users
 * @prop uid - User id
 * @prop childNodes - Internal property
 * @prop name - Internal property => Taken from slot content to display initials inside avatar
 *
 * @slot default - For user name
 *
 * @cssprop [--status-border-size] Border size of ef-online-status
 * @cssprop [--button-size] Edit button size
 * @cssprop [--slot-font-size] Name size
 * @cssprop [--icon-size] Edit button's icon size
 * @cssprop [--right] Used for calculation of online status position
 */

export default class ProfileAvatar extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    editable: { type: Boolean },
    hideName: { type: Boolean },
    hideStatus: { type: Boolean },
    label: { type: String },
    layout: { type: String, reflect: true },
    online: { type: Boolean },
    picture: { type: String },
    icon: { type: String },
    uid: { type: Number },
    childNodes: { state: true },
    name: { state: true },
  };

  constructor() {
    super();
    this.childNodes = [];
  }

  /** @internal */
  get shouldDisplayStatus() {
    return !this.hideStatus && !this.editable && !this.label;
  }

  setName() {
    this.name = this.childNodes
      .map(node => {
        return node.textContent ? node.textContent : '';
      })
      .join('')
      .trim();
  }

  handleClick() {
    alert('TODO: Add edit logic here');
  }

  renderButton() {
    return html`
      <button @click=${this.handleClick} aria-label="edit">
        <ef-icon name="pen"></ef-icon>
      </button>
    `;
  }

  handleSlotChange(e) {
    this.childNodes = e.target.assignedNodes({ flatten: true });

    this.setName();
  }

  renderOnlineStatus() {
    return html`
      <ef-online-status uid=${this.uid} .online=${this.online}>
      </ef-online-status>
    `;
  }

  render() {
    const classes = {
      'with-name': !this.hideName && !this.editable,
    };

    return html`
      <ef-avatar
        rounded
        icon=${ifDefined(this.icon)}
        .picture=${this.picture}
        .label=${this.label}
        .name=${this.name}
        class=${classMap(classes)}>
      </ef-avatar>
      ${this.editable && this.renderButton()}
      ${this.shouldDisplayStatus ? this.renderOnlineStatus() : null}
      <slot ?hidden=${this.hideName} @slotchange=${this.handleSlotChange}>
      </slot>
    `;
  }
}

customElements.get('xs-profile-avatar') ||
  customElements.define('xs-profile-avatar', ProfileAvatar);
